import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Receipts, RECEIPT_CATEGORIES } from "@/lib/api";
import { presetRanges } from "@/lib/bas";
import {
  Receipt as ReceiptIcon,
  ArrowUpRight,
  Search,
  FileSpreadsheet,
  X,
} from "lucide-react";
import { toast } from "sonner";

const fmt = (n) =>
  Number(n || 0).toLocaleString("en-AU", {
    style: "currency",
    currency: "AUD",
  });

const ReceiptsPage = () => {
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const search = params.get("q") || "";
  const category = params.get("category") || "all";

  const setSearch = (v) => {
    const next = new URLSearchParams(params);
    if (v) next.set("q", v);
    else next.delete("q");
    setParams(next, { replace: true });
  };
  const setCategory = (v) => {
    const next = new URLSearchParams(params);
    if (v && v !== "all") next.set("category", v);
    else next.delete("category");
    setParams(next, { replace: true });
  };

  const reload = async () => {
    setLoading(true);
    try {
      const [list, s] = await Promise.all([
        Receipts.list({
          search: search || undefined,
          category: category !== "all" ? category : undefined,
        }),
        Receipts.stats(),
      ]);
      setItems(list);
      setStats(s);
    } catch (e) {
      toast.error("Could not load receipts.");
    } finally {
      setLoading(false);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { reload(); }, [search, category]);

  const monthLabel = useMemo(() => {
    const d = new Date();
    return d.toLocaleString("en-AU", { month: "long", year: "numeric" });
  }, []);

  const [showExport, setShowExport] = useState(false);

  return (
    <div data-testid="receipts-page" className="fade-rise">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="swiss-label mb-3">Receipts</div>
          <h1 className="font-display font-black text-4xl sm:text-5xl tracking-tight leading-tight">
            Receipts
          </h1>
          <p className="text-neutral-600 mt-3 max-w-2xl">
            Snap any till receipt — Billy auto-fills vendor, total, GST and
            category, then keeps it cloud-saved for tax time.
          </p>
        </div>
        <button
          data-testid="export-bas-btn"
          onClick={() => setShowExport(true)}
          className="border-2 border-black bg-white text-black font-bold uppercase tracking-widest text-[11px] px-4 py-3 hover:bg-black hover:text-white transition-colors inline-flex items-center gap-2"
        >
          <FileSpreadsheet className="h-4 w-4" /> Export BAS
        </button>
      </div>

      {/* KPI */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-8">
        <div
          data-testid="kpi-month"
          className="bg-white border-2 border-black p-6"
        >
          <div className="swiss-label">{monthLabel}</div>
          <div className="font-display font-black text-3xl mono mt-2">
            {fmt(stats?.month_total)}
          </div>
          <div className="text-[11px] uppercase tracking-[0.2em] text-neutral-500 mt-1">
            {stats?.month_count ?? 0} receipts this month
          </div>
        </div>
        <div
          data-testid="kpi-all"
          className="bg-white border-2 border-black p-6"
        >
          <div className="swiss-label">All-time</div>
          <div className="font-display font-black text-3xl mono mt-2">
            {fmt(stats?.all_total)}
          </div>
          <div className="text-[11px] uppercase tracking-[0.2em] text-neutral-500 mt-1">
            {stats?.all_count ?? 0} receipts saved
          </div>
        </div>
        <Link
          to="/receipts/new"
          data-testid="new-receipt-cta"
          className="bg-black text-white border-2 border-black p-6 flex flex-col justify-between hover:bg-white hover:text-black transition-colors"
        >
          <div className="text-[11px] uppercase tracking-[0.25em] font-bold">
            Add a receipt
          </div>
          <div className="flex items-center justify-between mt-6">
            <div className="font-display font-black text-2xl leading-none">
              Snap it
              <span className="block text-sm font-normal opacity-70 mt-1">
                AI fills the rest.
              </span>
            </div>
            <ArrowUpRight className="h-6 w-6" strokeWidth={3} />
          </div>
        </Link>
      </div>

      {/* Search + filter */}
      <div className="mt-8 grid grid-cols-1 md:grid-cols-[1fr_auto] gap-3">
        <div className="relative">
          <Search className="h-4 w-4 absolute left-0 top-1/2 -translate-y-1/2 text-neutral-400" />
          <input
            data-testid="search-receipts"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="swiss-input w-full pl-7"
            placeholder="Search by vendor or note"
          />
        </div>
      </div>
      <div className="flex flex-wrap gap-2 mt-3" data-testid="category-filters">
        {["all", ...RECEIPT_CATEGORIES].map((c) => {
          const active = category === c;
          return (
            <button
              key={c}
              data-testid={`filter-${c}`}
              onClick={() => setCategory(c)}
              className={`px-3 py-2 border-2 text-[11px] uppercase tracking-widest font-bold transition-colors ${
                active
                  ? "bg-black text-white border-black"
                  : "bg-white text-black border-neutral-300 hover:border-black"
              }`}
            >
              {c === "all" ? "All" : c}
            </button>
          );
        })}
      </div>

      {/* List */}
      {loading ? (
        <div className="mt-10 text-sm text-neutral-500">Loading…</div>
      ) : items.length === 0 ? (
        <div
          data-testid="empty-state"
          className="mt-10 border-2 border-dashed border-neutral-300 p-10 text-center"
        >
          <ReceiptIcon className="h-8 w-8 mx-auto text-neutral-400" />
          <div className="font-display font-extrabold text-xl mt-3">
            No receipts yet
          </div>
          <div className="text-sm text-neutral-500 mt-1">
            Snap your first one — Billy reads it for you.
          </div>
          <button
            onClick={() => navigate("/receipts/new")}
            data-testid="empty-cta"
            className="mt-6 border-2 border-black bg-black text-white font-bold uppercase tracking-widest text-xs px-5 py-3 hover:bg-white hover:text-black transition-colors"
          >
            Snap a receipt
          </button>
        </div>
      ) : (
        <div className="mt-6 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {items.map((r) => (
            <Link
              key={r.id}
              to={`/receipts/${r.id}`}
              data-testid={`receipt-card-${r.id}`}
              className="bg-white border-2 border-black p-5 hover:-translate-y-0.5 transition-transform group block"
            >
              <div className="flex items-start justify-between gap-3">
                <div className="text-[10px] uppercase tracking-[0.2em] font-bold border border-black px-2 py-0.5">
                  {r.category}
                </div>
                <ArrowUpRight className="h-5 w-5 opacity-0 group-hover:opacity-100 transition-opacity" />
              </div>
              <div className="font-display font-black text-xl tracking-tight mt-3 line-clamp-1">
                {r.vendor}
              </div>
              <div className="text-[11px] uppercase tracking-[0.2em] text-neutral-500 mt-1">
                {r.receipt_date}
              </div>
              <div className="flex items-end justify-between gap-3 mt-4 pt-4 border-t border-neutral-200">
                <div>
                  <div className="swiss-label">Total</div>
                  <div className="font-display font-black text-2xl mono">
                    {fmt(r.total_amount)}
                  </div>
                </div>
                {r.gst_amount > 0 && (
                  <div className="text-right">
                    <div className="swiss-label">GST</div>
                    <div className="mono text-sm">{fmt(r.gst_amount)}</div>
                  </div>
                )}
              </div>
              {r.payment_method && (
                <div className="text-[11px] text-neutral-500 mt-3">
                  via {r.payment_method}
                </div>
              )}
            </Link>
          ))}
        </div>
      )}

      {showExport && (
        <ExportBasModal onClose={() => setShowExport(false)} />
      )}
    </div>
  );
};

const ExportBasModal = ({ onClose }) => {
  const navigate = useNavigate();
  const presets = useMemo(() => presetRanges(), []);
  const [from, setFrom] = useState(presets.thisQuarter.from);
  const [to, setTo] = useState(presets.thisQuarter.to);
  const [label, setLabel] = useState(presets.thisQuarter.label);
  const [busy, setBusy] = useState(false);

  const choose = (preset) => {
    setFrom(preset.from);
    setTo(preset.to);
    setLabel(preset.label);
  };

  const openSummary = () => {
    if (!from || !to) return toast.error("Pick a date range first");
    navigate(
      `/receipts/export?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}&label=${encodeURIComponent(label || "Receipts")}`,
    );
  };

  const downloadCsv = async () => {
    if (!from || !to) return toast.error("Pick a date range first");
    setBusy(true);
    try {
      await Receipts.exportCsv(from, to);
      toast.success("CSV downloaded.");
    } catch (e) {
      toast.error("Download failed.");
    } finally {
      setBusy(false);
    }
  };

  const presetItems = [
    { key: "thisQuarter", label: "This quarter", range: presets.thisQuarter },
    { key: "lastQuarter", label: "Last quarter", range: presets.lastQuarter },
    { key: "thisFY", label: "This FY", range: presets.thisFY },
    { key: "lastFY", label: "Last FY", range: presets.lastFY },
  ];

  return (
    <div
      data-testid="export-modal"
      className="fixed inset-0 z-40 bg-black/40 flex items-center justify-center px-4"
      onClick={onClose}
    >
      <div
        className="bg-white border-2 border-black w-full max-w-lg p-6 md:p-8"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-2">
          <div className="swiss-label">Export</div>
          <button
            onClick={onClose}
            data-testid="close-export"
            aria-label="Close"
            className="text-neutral-500 hover:text-black"
          >
            <X className="h-5 w-5" />
          </button>
        </div>
        <h2 className="font-display font-black text-2xl tracking-tight">
          Export receipts for BAS
        </h2>
        <p className="text-sm text-neutral-600 mt-2">
          Pick a quarter or financial year, then download a CSV for your
          accountant or open a print-ready summary.
        </p>

        <div className="grid grid-cols-2 gap-2 mt-6">
          {presetItems.map((p) => {
            const active = label === p.range.label;
            return (
              <button
                key={p.key}
                data-testid={`preset-${p.key}`}
                onClick={() => choose(p.range)}
                className={`text-left p-3 border-2 transition-colors ${
                  active
                    ? "bg-black text-white border-black"
                    : "bg-white text-black border-neutral-300 hover:border-black"
                }`}
              >
                <div className="font-bold uppercase tracking-widest text-[11px]">
                  {p.label}
                </div>
                <div
                  className={`text-[11px] mono mt-1 ${active ? "text-neutral-300" : "text-neutral-500"}`}
                >
                  {p.range.from} → {p.range.to}
                </div>
              </button>
            );
          })}
        </div>

        <div className="grid grid-cols-2 gap-3 mt-5">
          <label className="block">
            <div className="swiss-label mb-1">From</div>
            <input
              data-testid="custom-from"
              type="date"
              className="swiss-input w-full"
              value={from}
              onChange={(e) => {
                setFrom(e.target.value);
                setLabel("Custom range");
              }}
            />
          </label>
          <label className="block">
            <div className="swiss-label mb-1">To</div>
            <input
              data-testid="custom-to"
              type="date"
              className="swiss-input w-full"
              value={to}
              onChange={(e) => {
                setTo(e.target.value);
                setLabel("Custom range");
              }}
            />
          </label>
        </div>

        <div className="mt-6 flex flex-wrap gap-3">
          <button
            data-testid="csv-btn"
            onClick={downloadCsv}
            disabled={busy}
            className="border-2 border-black bg-white text-black font-bold uppercase tracking-widest text-[11px] px-4 py-3 hover:bg-black hover:text-white transition-colors disabled:opacity-50"
          >
            Download CSV
          </button>
          <button
            data-testid="open-summary-btn"
            onClick={openSummary}
            className="border-2 border-black bg-black text-white font-bold uppercase tracking-widest text-[11px] px-4 py-3 hover:bg-white hover:text-black transition-colors"
          >
            Open print summary →
          </button>
        </div>
      </div>
    </div>
  );
};

export default ReceiptsPage;
